/**
 * How near a renewal is, in words.
 *
 * A date alone ("5 Sep 2026") makes a reader do arithmetic against today
 * before they know whether to care. The panel shows both: the date, and how
 * far off it is.
 *
 * Comparison is by CALENDAR DAY, not by elapsed hours. An agreement renewing
 * later today is "today", not "in 0 days", and one renewing tomorrow morning
 * is "tomorrow" whether it is now 9am or 11pm — which is how a person reads a
 * renewal date. crm-api sends these as `YYYY-MM-DD` with no time at all, so
 * hour-based arithmetic would only ever be measuring the reader's clock.
 */

/** Whole days from `today` to `on`, both floored to local midnight. Null when
 * the date is missing or unparseable — the caller then shows the raw value
 * rather than inventing a distance. */
export function daysUntil(on: string | null | undefined, today = new Date()): number | null {
  if (!on) return null;
  const target = new Date(on);
  if (Number.isNaN(target.getTime())) return null;

  const a = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / 86_400_000);
}

/** "today" / "tomorrow" / "in 12 days" / "3 days ago". Null when the distance
 * is unknowable. */
export function timingLabel(on: string | null | undefined, today = new Date()): string | null {
  const days = daysUntil(on, today);
  if (days === null) return null;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  if (days > 1) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
}
